import {compact, partition} from 'lodash';
import React, {FC, ReactNode, useRef, useState} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {CancelToken} from '../../../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {isTeammateNamespace} from '../../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {DropdownCategory} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownCategory';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemCheckbox} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemCheckbox';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {AsyncSearchDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/search/asyncSearchDropdown';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {CardGroupModel} from '../../../../models/api/cardGroupModel';
import {DropdownItemAccentIcon} from '../dropdownItemAccentIcon';
import {TypedRowValue} from '../source/dropdownSource';

/*
 * Constants.
 */

const dropdownMaxHeight = 360; // px.
enum SearchCardGroupsDropdownRowTypesEnum {
  CATEGORY = 'CATEGORY',
  NEW_ACTION = 'NEW_ACTION',
  GROUP = 'GROUP',
}

/*
 * Props.
 */

interface SearchCardGroupsDropdownProps {
  selectedCardGroupIds: ReadonlyArray<number>;
  popoverProps: PopoverTransferProps;

  onNewGroupClick?: () => void;
  onFetchCardGroupsAsync: (token: CancelToken) => Promise<ReadonlyArray<CardGroupModel>>;
  onSelect: (model: CardGroupModel) => void;
  onRemove: (model: CardGroupModel) => void;
}

type SearchCardGroupsDropdownRowTypedValue =
  | TypedRowValue<SearchCardGroupsDropdownRowTypesEnum.CATEGORY, ReactNode>
  | TypedRowValue<SearchCardGroupsDropdownRowTypesEnum.NEW_ACTION, undefined>
  | TypedRowValue<SearchCardGroupsDropdownRowTypesEnum.GROUP, CardGroupModel>;

/*
 * Style.
 */

const StyledDropdownItemContent = styled(DropdownItemContent)`
  display: flex;
  flex-flow: row;
  overflow: hidden;
`;

interface StyledTooltipOverflowConditionProps {
  $isSelected: boolean;
}
const StyledTooltipOverflowCondition = styled(TooltipOverflowCondition)<StyledTooltipOverflowConditionProps>`
  direction: ltr;

  ${(p) =>
    p.$isSelected &&
    css`
      font-weight: ${fontWeights.bold};
    `}
`;

const StyledDropdownItemAccentIcon = styled(DropdownItemAccentIcon)`
  padding: 4px;
`;

const StyledIcon = styled(Icon)`
  margin: 4px 0 0 4px;
`;

/*
 * Component.
 */

export const SearchCardGroupsDropdown: FC<SearchCardGroupsDropdownProps> = (props) => {
  const {selectedCardGroupIds, popoverProps, onNewGroupClick, onFetchCardGroupsAsync, onSelect, onRemove} =
    props;

  const [query, setQuery] = useState('');
  const dropdownItemsRef = useRef<ReadonlyArray<SearchCardGroupsDropdownRowTypedValue>>();

  return (
    <AsyncSearchDropdown
      {...popoverProps}
      headerTitle={<Trans id="search-card-groups-dropdown-lists-title">Lists</Trans>}
      query={query}
      onQueryChange={setQuery}
      renderRow={(item: SearchCardGroupsDropdownRowTypedValue) => {
        if (item.type === SearchCardGroupsDropdownRowTypesEnum.CATEGORY) {
          return renderCategory(item.value);
        }
        if (item.type === SearchCardGroupsDropdownRowTypesEnum.NEW_ACTION && onNewGroupClick) {
          return renderNewGroupAction(onNewGroupClick);
        }
        if (item.type === SearchCardGroupsDropdownRowTypesEnum.GROUP) {
          return renderCardGroup(item.value, selectedCardGroupIds, onSelect, onRemove);
        }
        return null;
      }}
      onRequestData={async (_searchQuery, _data, cancelToken) => {
        // If we have dropdown items, we already have loaded all the groups.
        if (dropdownItemsRef.current) {
          return {
            data: filterItems(dropdownItemsRef.current, query),
            canRequestMore: false,
          };
        }

        const response = await onFetchCardGroupsAsync(cancelToken);

        // Order the fetched card groups with checked items first.
        const [selectedGroups, unselectedGroups] = partition(response, (group) =>
          selectedCardGroupIds.includes(group.id),
        );
        const items = buildDropdownItems([...selectedGroups, ...unselectedGroups], Boolean(onNewGroupClick));
        dropdownItemsRef.current = items;
        return {
          data: filterItems(items, query),
          canRequestMore: false,
        };
      }}
      maxHeight={dropdownMaxHeight}
      searchInputShouldIncludeSearchIcon
    />
  );
};

/*
 * Helpers.
 */

function filterItems(items: ReadonlyArray<SearchCardGroupsDropdownRowTypedValue>, query: string) {
  if (!query) {
    return items;
  }

  const queryInLowerCase = query.toLowerCase();
  const filteredGroups = items.filter(
    (item) =>
      item.type === SearchCardGroupsDropdownRowTypesEnum.GROUP &&
      item.value.name.toLowerCase().includes(queryInLowerCase),
  );

  if (filteredGroups.length === 0) {
    return [];
  }
  return [
    ...items.filter((item) => item.type === SearchCardGroupsDropdownRowTypesEnum.CATEGORY),
    ...filteredGroups,
  ];
}

function buildDropdownItems(
  cardGroups: ReadonlyArray<CardGroupModel>,
  shouldRenderCreate?: boolean,
): ReadonlyArray<SearchCardGroupsDropdownRowTypedValue> {
  const createHeaderAndAction = [
    buildDropdownCategory(<Trans id="search-card-groups-dropdown-create-header">Create</Trans>),
    buildDropdownNewGroupAction(),
  ];
  return compact([
    ...(shouldRenderCreate ? createHeaderAndAction : []),
    buildDropdownCategory(<Trans id="search-card-groups-dropdown-all-lists-header">All lists</Trans>),
    ...cardGroups.map(buildDropdownGroup),
  ]);
}

function buildDropdownGroup(value: CardGroupModel): SearchCardGroupsDropdownRowTypedValue {
  return {
    type: SearchCardGroupsDropdownRowTypesEnum.GROUP,
    value,
  };
}

function buildDropdownCategory(value: React.ReactNode): SearchCardGroupsDropdownRowTypedValue {
  return {
    type: SearchCardGroupsDropdownRowTypesEnum.CATEGORY,
    value,
  };
}

function buildDropdownNewGroupAction(): SearchCardGroupsDropdownRowTypedValue {
  return {
    type: SearchCardGroupsDropdownRowTypesEnum.NEW_ACTION,
    value: undefined,
  };
}

/*
 * Render Helpers.
 */

function renderCategory(category: ReactNode) {
  return <DropdownCategory>{category}</DropdownCategory>;
}

function renderNewGroupAction(onNewGroupClick: () => void) {
  return (
    <DropdownItem
      interactionId="search-card-groups-dropdown-new-group"
      onSelect={(event) => {
        event.preventDefault();
        onNewGroupClick();
      }}
    >
      <StyledDropdownItemAccentIcon label={null} iconName="newTiny" />
      <DropdownItemContent>
        <Trans id="search-card-groups-dropdown-new-list-action">New list</Trans>
      </DropdownItemContent>
    </DropdownItem>
  );
}

function renderCardGroup(
  group: CardGroupModel,
  selectedGroupIds: ReadonlyArray<number>,
  onSelect: (model: CardGroupModel) => void,
  onRemove: (model: CardGroupModel) => void,
) {
  const isSelected = selectedGroupIds.includes(group.id);
  return (
    <UserThemeConsumer>
      {(theme) => (
        <TooltipCoordinator render={() => group.name} placement="top">
          <DropdownItem
            interactionId={`contact-details-group-${group.id}`}
            onSelect={(event) => {
              // Do not close the dropdown
              event.preventDefault();
              if (isSelected) {
                onRemove(group);
              } else {
                onSelect(group);
              }
            }}
          >
            <StyledDropdownItemContent>
              <StyledTooltipOverflowCondition $isSelected={isSelected}>
                {group.name}
              </StyledTooltipOverflowCondition>
              {isTeammateNamespace(group.namespace) && (
                <StyledIcon name="lockFilledNewTiny" color={theme.greys.shade70} />
              )}
            </StyledDropdownItemContent>
            <DropdownItemCheckbox isChecked={isSelected} />
          </DropdownItem>
        </TooltipCoordinator>
      )}
    </UserThemeConsumer>
  );
}
